import React, { useState, useEffect, useCallback } from 'react';
import { RotateCcw } from 'lucide-react';
import { Resource, UIComponent } from '../../types';
import { 
  getColorValue, 
  getShapeIcon 
} from './utils/sortingCanvasUtils';

interface SortingCanvasComponentProps {
  resource: Resource;
  component: UIComponent;
  onAction?: (action: string, data?: any) => void;
}

interface ObjectConfig {
  type: string;
  count: number;
  color: string;
  size?: string;
}

interface SortableObject {
  id: number;
  x: number;
  y: number;
  type: string;
  color: string;
  size: string;
  category: string | null;
  isSelected: boolean;
}

interface Category {
  id: string;
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  objects: SortableObject[];
  sortingCriteria: 'color' | 'shape' | 'size' | 'custom';
  availableValues?: string[];
}

export const SortingCanvasComponent: React.FC<SortingCanvasComponentProps> = ({ 
  resource, 
  component, 
  onAction 
}) => {
  const [objects, setObjects] = useState<SortableObject[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [evaluationResult, setEvaluationResult] = useState<any>(null);



  // Get configuration from resource - make responsive
  const [canvasDimensions, setCanvasDimensions] = useState({ width: 490, height: 350 });
  const [objectSize, setObjectSize] = useState(30);
  
  // Canvas dimensions - reduced sizes for better Resource Panel fit
  useEffect(() => {
    const updateCanvasSize = () => {
      const containerWidth = window.innerWidth;
      
      if (containerWidth < 768) {
        // Mobile: compact canvas
        setCanvasDimensions({ width: 280, height: 200 });
        setObjectSize(20);
      } else if (containerWidth < 1024) {
        // Tablet: medium canvas
        setCanvasDimensions({ width: 400, height: 300 });
        setObjectSize(24);
      } else {
        // Desktop: standard canvas
        setCanvasDimensions({ width: 500, height: 350 });
        setObjectSize(26);
      }
    };
    
    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, []);
  
  // Get settings from resource
  const showCount = resource.showCount !== undefined ? resource.showCount : true;
  const allowInteraction = resource.allowInteraction !== undefined ? resource.allowInteraction : true;
  const question = resource.question || 'Triez ces objets selon la couleur, la forme ou la taille';
  
  // Get objects and categories from resource with fallbacks
  

  const generateObjects = useCallback(() => {
    try {
      const newObjects: SortableObject[] = [];
      let objectId = 0;
      const objectConfigs: ObjectConfig[] = resource.objects || [
        { type: 'circle', count: 3, color: 'red', size: 'medium' },
        { type: 'square', count: 3, color: 'blue', size: 'medium' },
        { type: 'triangle', count: 3, color: 'green', size: 'medium' }
      ];

      objectConfigs.forEach((config) => {
        for (let i = 0; i < config.count; i++) {
                  newObjects.push({
          id: objectId++,
          x: Math.random() * (canvasDimensions.width - 200) + 20,
          y: Math.random() * (canvasDimensions.height - 220) + 20,
            type: config.type,
            color: config.color,
            size: config.size || 'medium',
            category: null,
            isSelected: false
          });
        }
      });

      setObjects(newObjects);
      setError(null);
    } catch (err) {
      setError('Erreur lors de la génération des objets');
      console.error('Error generating objects:', err);
    }
  }, [resource.objects, canvasDimensions.width, canvasDimensions.height]);

  const generateCategories = useCallback(() => {
    try {
      const newCategories: Category[] = [];
      // Reduced category dimensions for smaller canvas
      const categoryWidth = 100;
      const categoryHeight = 60;
      const spacing = 15;
      
      // Simple horizontal layout
      const startX = 15;
      const startY = canvasDimensions.height - categoryHeight - 15;
      const categoryConfigs: any[] = resource.categories || [
        { id: 'couleur', name: 'Couleur', color: '#FF6B6B' },
        { id: 'forme', name: 'Forme', color: '#4ECDC4' },
        { id: 'taille', name: 'Taille', color: '#45B7D1' }
      ];

      categoryConfigs.forEach((categoryConfig, index) => {
        // Handle both string and object category configurations
        const categoryId = typeof categoryConfig === 'string' ? categoryConfig : categoryConfig.id || `category_${index}`;
        const categoryName = typeof categoryConfig === 'string' ? categoryConfig : categoryConfig.name || `Catégorie ${index + 1}`;
        const categoryColor = typeof categoryConfig === 'string' ? getCategoryColor(categoryConfig) : (categoryConfig.color || getCategoryColor('default'));

        // Simple horizontal positioning
        const x = startX + index * (categoryWidth + spacing);
        const y = startY;

              // Determine sorting criteria based on category name or custom config
      let sortingCriteria: 'color' | 'shape' | 'size' | 'custom' = 'custom';
      let availableValues: string[] = [];
      
      if (typeof categoryConfig === 'object') {
        if (categoryConfig.sortingCriteria) {
          sortingCriteria = categoryConfig.sortingCriteria;
        }
        if (categoryConfig.availableValues) {
          availableValues = categoryConfig.availableValues;
        }
      } else {
        // Auto-detect based on category name
        const nameLower = categoryName.toLowerCase();
        if (nameLower.includes('couleur') || nameLower.includes('color')) {
          sortingCriteria = 'color';
        } else if (nameLower.includes('forme') || nameLower.includes('shape')) {
          sortingCriteria = 'shape';
        } else if (nameLower.includes('taille') || nameLower.includes('size')) {
          sortingCriteria = 'size';
        }
      }

      newCategories.push({
        id: categoryId,
        name: categoryName,
        x: x,
        y: y,
        width: categoryWidth,
        height: categoryHeight,
        color: categoryColor,
        objects: [],
        sortingCriteria: sortingCriteria,
        availableValues: availableValues
      });
      });

      setCategories(newCategories);
      setError(null);
    } catch (err) {
      setError('Erreur lors de la génération des catégories');
      console.error('Error generating categories:', err);
    }
  }, [resource.categories, canvasDimensions.height]);
  
  // Regenerate objects and categories when dimensions change
  useEffect(() => {
    if (objects.length > 0 || categories.length > 0) {
      generateObjects();
      generateCategories();
    }
  }, [objects.length, categories.length, generateObjects, generateCategories]);

  const getCategoryColor = (categoryName: string): string => {
    const colorMap: Record<string, string> = {
      'couleur': '#FF6B6B',
      'forme': '#4ECDC4',
      'taille': '#45B7D1',
      'nature': '#96CEB4',
      'default': '#DDA0DD'
    };
    return colorMap[categoryName] || colorMap.default;
  };



  const handleObjectClick = (object: SortableObject) => {
    if (!allowInteraction) return;
    
    // Toggle selection
    setObjects(prev => prev.map(obj => 
      obj.id === object.id 
        ? { ...obj, isSelected: !obj.isSelected }
        : { ...obj, isSelected: false } // Deselect other objects
    ));
  };

  const handleCategoryClick = (category: Category) => {
    if (!allowInteraction) return;
    
    // Find the selected object
    const selectedObject = objects.find(obj => obj.isSelected);
    if (!selectedObject) return;
    
    // Check if object is already in this category
    if (selectedObject.category === category.id) {
      // Remove from category
      setObjects(prev => prev.map(obj => 
        obj.id === selectedObject.id 
          ? { ...obj, category: null, isSelected: false }
          : obj
      ));
      
      // Remove from category objects
      setCategories(prev => prev.map(cat => 
        cat.id === category.id
          ? { ...cat, objects: cat.objects.filter(obj => obj.id !== selectedObject.id) }
          : cat
      ));
    } else {
      // Add to category
      setObjects(prev => prev.map(obj => 
        obj.id === selectedObject.id 
          ? { ...obj, category: category.id, isSelected: false }
          : obj
      ));
      
      // Add to category objects
      setCategories(prev => prev.map(cat => 
        cat.id === category.id
          ? { ...cat, objects: [...cat.objects, { ...selectedObject, category: category.id }] }
          : cat
      ));
    }
    
    // Clear selection
    setObjects(prev => prev.map(obj => ({ ...obj, isSelected: false })));
  };





  const resetCanvas = () => {
    generateObjects();
    setCategories(prev => prev.map(cat => ({ ...cat, objects: [] })));
    onAction?.('canvas_reset', {});
  };

  const getSortedCount = () => {
    return objects.filter(obj => obj.category !== null).length;
  };

  const getTotalCount = () => {
    return objects.length;
  };

  const evaluateSorting = () => {
    if (objects.length === 0) return { isCorrect: false, score: 0, feedback: 'Aucun objet à évaluer' };
    
    let correctSorts = 0;
    let totalSorts = 0;
    const feedback: string[] = [];

    // Check each category for correct sorting
    categories.forEach(category => {
      const categoryObjects = objects.filter(obj => obj.category === category.id);
      if (categoryObjects.length === 0) return;

      totalSorts += categoryObjects.length;
      
      // Check if objects in this category match the expected criteria
      const isCorrectlySorted = categoryObjects.every(obj => {
        switch (category.sortingCriteria) {
          case 'color':
            // All objects in color category should have the same color
            // AND the color should be in the available values (if specified)
            const colorMatch = categoryObjects.every(otherObj => otherObj.color === obj.color);
            const colorAllowed = !category.availableValues || category.availableValues.length === 0 || 
                                category.availableValues.includes(obj.color);
            return colorMatch && colorAllowed;
          
          case 'shape':
            // All objects in shape category should have the same type
            // AND the type should be in the available values (if specified)
            const shapeMatch = categoryObjects.every(otherObj => otherObj.type === obj.type);
            const shapeAllowed = !category.availableValues || category.availableValues.length === 0 || 
                                category.availableValues.includes(obj.type);
            return shapeMatch && shapeAllowed;
          
          case 'size':
            // All objects in size category should have the same size
            // AND the size should be in the available values (if specified)
            const sizeMatch = categoryObjects.every(otherObj => otherObj.size === obj.size);
            const sizeAllowed = !category.availableValues || category.availableValues.length === 0 || 
                               category.availableValues.includes(obj.size);
            return sizeMatch && sizeAllowed;
          
          case 'custom':
          default:
            // For custom categories, consider it correct if objects are grouped
            return true;
        }
      });

      if (isCorrectlySorted) {
        correctSorts += categoryObjects.length;
        const criteriaText = category.sortingCriteria === 'custom' ? 'grouper' : `par ${category.sortingCriteria}`;
        feedback.push(`✅ ${category.name}: ${categoryObjects.length} objet(s) correctement trié(s ${criteriaText})`);
      } else {
        const criteriaText = category.sortingCriteria === 'custom' ? 'les critères' : `par ${category.sortingCriteria}`;
        feedback.push(`❌ ${category.name}: Tri incorrect - vérifiez ${criteriaText}`);
      }
    });

    const score = totalSorts > 0 ? Math.round((correctSorts / totalSorts) * 100) : 0;
    const isCorrect = score === 100 && totalSorts === objects.length;

    return {
      isCorrect,
      score,
      feedback,
      correctSorts,
      totalSorts
    };
  };

  useEffect(() => {
    generateObjects();
    generateCategories();
  }, [generateObjects, generateCategories]);

  // Show error if something went wrong
  if (error) {
    return (
      <div className="sorting-canvas-component w-full max-w-full p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">Erreur</h3>
          <p className="text-red-600 text-sm">{error}</p>
          <button 
            onClick={() => {
              setError(null);
              generateObjects();
              generateCategories();
            }}
            className="mt-2 px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sorting-canvas-component w-full max-w-full">
      {/* Header */}
      <div className="mb-4 px-2 sm:px-0">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
          {resource.name || 'Canvas de Tri et Classement'}
        </h3>
        <p className="text-xs sm:text-sm text-gray-600 mb-2">
          {resource.description || 'Triez les objets selon les critères donnés'}
        </p>
        {question && (
          <p className="text-sm sm:text-md font-medium text-blue-600 mb-2">
            {question}
          </p>
        )}
        {showCount && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 text-xs sm:text-sm text-gray-600">
            <span>Objets triés: {getSortedCount()}/{getTotalCount()}</span>
            <button
              onClick={resetCanvas}
              className="flex items-center space-x-1 text-blue-600 hover:text-blue-800"
            >
              <RotateCcw className="w-4 h-4" />
              <span>Réinitialiser</span>
            </button>
            <button
              onClick={() => {
                const result = evaluateSorting();
                setEvaluationResult(result);
              }}
              className="flex items-center space-x-1 text-green-600 hover:text-green-800"
            >
              <span>Évaluer</span>
            </button>
          </div>
        )}
      </div>

      {/* Canvas */}
      <div className="w-full flex justify-center">
        <div 
          className="relative border-2 border-gray-300 rounded-lg bg-gray-50 overflow-auto"
          style={{ 
            width: canvasDimensions.width, 
            height: canvasDimensions.height,
            maxHeight: '50vh', // Reduced height for smaller canvas
            scrollbarWidth: 'thin',
            scrollbarColor: '#CBD5E0 #F7FAFC'
          }}
        >
        {/* Objects */}
        {objects.map((object) => (
          <div
            key={object.id}
            className={`absolute cursor-pointer transition-all duration-200 ${
              object.category ? 'opacity-60' : 'opacity-100'
            } ${object.isSelected ? 'ring-4 ring-blue-400 ring-opacity-50' : ''}`}
            style={{
              left: object.x,
              top: object.y,
              width: objectSize,
              height: objectSize
            }}
            onClick={() => handleObjectClick(object)}
          >
            <div
              className={`w-full h-full border-2 border-gray-800 flex items-center justify-center text-white font-bold text-sm ${
                object.type === 'circle' ? 'rounded-full' :
                object.type === 'square' ? 'rounded-none' :
                object.type === 'triangle' ? 'triangle' : 'rounded-full'
              }`}
              style={{ backgroundColor: getColorValue(object.color) }}
            >
              {getShapeIcon(object.type)}
            </div>
          </div>
        ))}

        {/* Categories */}
        {categories.map((category) => (
          <div
            key={category.id}
            className={`absolute border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-all duration-200 hover:scale-105 ${
              objects.some(obj => obj.isSelected) ? 'ring-2 ring-blue-400 ring-opacity-75' : ''
            }`}
            style={{
              left: category.x,
              top: category.y,
              width: category.width,
              height: category.height,
              borderColor: category.color,
              backgroundColor: `${category.color}20`
            }}
            onClick={() => handleCategoryClick(category)}
          >
            <div 
              className="text-sm font-semibold mb-2"
              style={{ color: category.color }}
            >
              {String(category.name || '').toUpperCase()}
            </div>
            <div className="text-xs text-gray-500 mb-1">
              {category.sortingCriteria === 'color' ? '🎨 Par couleur' : 
               category.sortingCriteria === 'shape' ? '🔷 Par forme' : 
               category.sortingCriteria === 'size' ? '📏 Par taille' : '📦 Grouper'}
            </div>
            <div className="text-xs text-gray-600">
              {category.objects.length} objet(s)
            </div>
            
            {/* Show sorted objects in category */}
            {category.objects.length > 0 && (
              <div className="mt-2 flex flex-wrap justify-center gap-1">
                {category.objects.slice(0, 3).map((obj) => (
                  <div
                    key={obj.id}
                    className="w-3 h-3 rounded-full border border-gray-800"
                    style={{ backgroundColor: getColorValue(obj.color) }}
                  />
                ))}
                {category.objects.length > 3 && (
                  <span className="text-xs text-gray-500">+{category.objects.length - 3}</span>
                )}
              </div>
            )}
          </div>
        ))}
        </div>
      </div>

      {/* Evaluation Results */}
      {evaluationResult && (
        <div className={`mt-4 p-3 rounded-lg border ${
          evaluationResult.isCorrect 
            ? 'bg-green-50 border-green-200 text-green-800' 
            : 'bg-yellow-50 border-yellow-200 text-yellow-800'
        }`}>
          <h4 className="font-medium mb-2">
            {evaluationResult.isCorrect ? '🎉 Excellent !' : '📝 Résultats de l\'évaluation'}
          </h4>
          <div className="text-sm space-y-1">
            <p><strong>Score:</strong> {evaluationResult.score}%</p>
            <p><strong>Objets correctement triés:</strong> {evaluationResult.correctSorts}/{evaluationResult.totalSorts}</p>
            {evaluationResult.feedback && (
              <div className="mt-2">
                <p className="font-medium mb-1">Détails:</p>
                <ul className="space-y-1">
                  {evaluationResult.feedback.map((item: string, index: number) => (
                    <li key={index} className="text-xs">{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <button
            onClick={() => setEvaluationResult(null)}
            className="mt-2 px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200"
          >
            Fermer
          </button>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-4 p-2 sm:p-3 bg-blue-50 rounded-lg">
        <h4 className="font-medium text-blue-900 mb-2 text-sm sm:text-base">Instructions:</h4>
        <ul className="text-xs sm:text-sm text-blue-800 space-y-1">
          <li>• Cliquez sur un objet pour le sélectionner (il sera entouré de bleu)</li>
          <li>• Cliquez sur une catégorie pour y placer l'objet sélectionné</li>
          <li>• Cliquez à nouveau sur la même catégorie pour retirer l'objet</li>
          <li>• <strong>Critères de tri configurés:</strong></li>
          {categories.map(category => (
            <li key={category.id} className="ml-4">
              - <strong>{category.name}:</strong> {category.sortingCriteria === 'color' ? 'Par couleur' : 
                category.sortingCriteria === 'shape' ? 'Par forme' : 
                category.sortingCriteria === 'size' ? 'Par taille' : 'Grouper librement'}
              {category.sortingCriteria !== 'custom' && category.availableValues && category.availableValues.length > 0 && (
                <span className="text-blue-600">
                  {' '}(Valeurs acceptées: {category.availableValues.join(', ')})
                </span>
              )}
            </li>
          ))}
          <li>• Cliquez sur "Évaluer" pour vérifier votre tri</li>
          <li>• Utilisez le bouton "Réinitialiser" pour recommencer</li>
          <li>• Faites défiler pour voir tout le contenu du canvas</li>
        </ul>
      </div>
    </div>
  );
};
